import {
  resolveCatalogProductName,
  type InjectGateDecision,
  type InjectGateResult,
} from './injectGate';

const DECISIONS: InjectGateDecision[] = ['inject', 'reject', 'hold'];

function asGateCheck(value: unknown): InjectGateResult['brand'] {
  if (value === 'pass' || value === 'warn' || value === 'fail') return value;
  return 'warn';
}

/** Read an inject-gate blob stored on a listing JSON object. */
export function parseStoredInjectGate(raw: unknown): InjectGateResult | null {
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;
  const decision = rec.decision;
  if (typeof decision !== 'string' || !DECISIONS.includes(decision as InjectGateDecision)) {
    return null;
  }
  return {
    brand: asGateCheck(rec.brand),
    product: asGateCheck(rec.product),
    infringement: asGateCheck(rec.infringement),
    invalid: Boolean(rec.invalid),
    decision: decision as InjectGateDecision,
    summary: typeof rec.summary === 'string' ? rec.summary : 'Stored inject gate',
    reasons: Array.isArray(rec.reasons) ? rec.reasons.map(String) : [],
    selected_product: typeof rec.selected_product === 'string' ? rec.selected_product : null,
    product_corrected: Boolean(rec.product_corrected),
    brand_ip_merchandise: Boolean(rec.brand_ip_merchandise),
    article_category:
      typeof rec.article_category === 'string' && rec.article_category.trim()
        ? rec.article_category.trim()
        : null,
  };
}

/**
 * Reuse a stored gate when Auto-process can skip the LLM.
 * Hold always re-runs. A catalog inject is reused only for a clear product match
 * whose selected_product still exists in the current brand catalog. A brand-IP
 * merchandise inject is reused without a catalog product.
 */
export function canReuseInjectGate(
  gate: InjectGateResult | null,
  catalogNames: string[]
): InjectGateResult | null {
  if (!gate) return null;
  if (gate.decision === 'hold') return null;
  if (gate.decision === 'inject') {
    if (gate.brand_ip_merchandise) {
      return { ...gate, selected_product: null, product_corrected: false };
    }
    if (gate.product !== 'pass') return null;
    const resolved = resolveCatalogProductName(gate.selected_product, catalogNames);
    if (!resolved) return null;
    return { ...gate, selected_product: resolved };
  }
  return gate;
}
